import React, {useEffect, useRef, useState} from 'react'
import {Button, Modal} from 'react-bootstrap-v5'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {KTSVG} from 'app/utils/helpers'
import {MAX_SIZE_SERVICE_ICON} from 'app/utils/common/constants'
import {useAppDispatch} from 'setup/redux/Hooks'
import {serviceManagementAction} from '../../redux/ServiceManagementSlice'
import {CategoryPaymentConfig, CentroCategoryInfoDetailQry} from '../../models'
import TransportationConfig from '../transportation-config/TransportationConfig'
import './style.scss'
import CategoryPaymentSettings from './CategoryPaymentSettings'

const ICON_BASE = '/assets/images/icons'

const ICONS = {
  close: `${ICON_BASE}/close-line.svg`,
  info: `${ICON_BASE}/info-circle-line.svg`,
  image: `${ICON_BASE}/image-line.svg`,
}

const STEP_KEYS = [
  'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.STEP_CATEGORY_INFO',
  'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.STEP_PAYMENT',
  'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.STEP_TRANSPORTATION',
]

const STEP_CATEGORY_INFO = 0
const STEP_PAYMENT = 1
const STEP_TRANSPORTATION = 2

export type ServiceCategoryModalMode = 'add' | 'edit'

export interface ServiceCategoryValues {
  nameEn: string
  nameAr: string
  descriptionEn: string
  descriptionAr: string
  isActive: boolean
}

const EMPTY_VALUES: ServiceCategoryValues = {
  nameEn: '',
  nameAr: '',
  descriptionEn: '',
  descriptionAr: '',
  isActive: false,
}

interface ServiceCategoryModalProps {
  isShow: boolean
  mode?: ServiceCategoryModalMode
  categoryId?: string
  initialValues?: Partial<ServiceCategoryValues>
  onHide: () => void
  onSaved?: () => void
  onOpenAdvanced?: () => void
}

const ServiceCategoryModal: React.FC<ServiceCategoryModalProps> = ({
  isShow,
  mode = 'add',
  categoryId,
  initialValues,
  onHide,
  onSaved,
  onOpenAdvanced,
}) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Callers pass a fresh object literal on every render - reset the form on open only
  const initialValuesRef = useRef<Partial<ServiceCategoryValues> | undefined>(initialValues)
  initialValuesRef.current = initialValues
  const [values, setValues] = useState<ServiceCategoryValues>(EMPTY_VALUES)
  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState<string>('')
  const [fileError, setFileError] = useState<string>('')
  const [touchedNames, setTouchedNames] = useState<{nameEn: boolean; nameAr: boolean}>({
    nameEn: false,
    nameAr: false,
  })
  const [formError, setFormError] = useState<string>('')
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const isSavingRef = useRef<boolean>(false)
  const [paymentConfig, setPaymentConfig] = useState<CategoryPaymentConfig | null>(null)
  const [currentStep, setCurrentStep] = useState<number>(STEP_CATEGORY_INFO)
  const [isEditable, setIsEditable] = useState<boolean>(true)
  const [transportDispatchMode, setTransportDispatchMode] = useState<string | null>(null)

  useEffect(() => {
    if (!isShow) {
      return
    }
    setValues({...EMPTY_VALUES, ...initialValuesRef.current})
    setPaymentConfig(null)
    setCurrentStep(STEP_CATEGORY_INFO)
    setIconFile(null)
    setIconPreview('')
    setFileError('')
    setFormError('')
    setTouchedNames({nameEn: false, nameAr: false})
    setIsEditable(true)
    setTransportDispatchMode(null)
    setIsSaving(false)
    isSavingRef.current = false
  }, [isShow])

  useEffect(() => {
    if (!isShow || mode !== 'edit' || !categoryId) {
      return
    }
    setIsLoadingDetail(true)
    dispatch(
      serviceManagementAction.getCentroCategoryInfoDetail({
        id: categoryId,
        callback: (res: CentroCategoryInfoDetailQry | null, err: string | null) => {
          setIsLoadingDetail(false)
          if (!res) {
            setFormError(err || t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.LOAD_FAILED'))
            return
          }
          setValues({
            nameEn: res.nameEn || '',
            nameAr: res.nameAr || '',
            descriptionEn: res.descriptionEn || '',
            descriptionAr: res.descriptionAr || '',
            isActive: res.status,
          })
          setPaymentConfig(res.paymentConfig ?? null)
          setIconPreview(res.icon || '')
          setIsEditable(res.isEditable)
          setTransportDispatchMode(res.transportationConfig?.dispatchMode ?? null)
        },
      })
    )
  }, [isShow, mode, categoryId, dispatch, t])

  const setField = <K extends keyof ServiceCategoryValues>(
    key: K,
    value: ServiceCategoryValues[K]
  ) => setValues((current) => ({...current, [key]: value}))

  // The icon must be a PNG, at most 500 KB, with a 1:1 aspect ratio (backend rejects anything else)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }

    const isValidFormat = file.type === 'image/png'
    const isValidSize = file.size <= MAX_SIZE_SERVICE_ICON

    const reader = new FileReader()
    reader.onload = (loaded: ProgressEvent<FileReader>) => {
      const dataUrl = String(loaded.target?.result || '')
      const image = new Image()
      image.onload = () => {
        const isValidRatio = image.width === image.height
        const errorCount = [isValidFormat, isValidSize, isValidRatio].filter(
          (valid) => !valid
        ).length

        if (errorCount === 0) {
          setFileError('')
          setIconFile(file)
          setIconPreview(dataUrl)
          return
        }

        setIconFile(null)
        setIconPreview('')
        if (errorCount > 1) {
          setFileError(
            t(
              errorCount === 2
                ? 'SERVICE_MANAGEMENT_PAGE.SERVICE.HAS_2_DIFFERENT_FORMAT'
                : 'SERVICE_MANAGEMENT_PAGE.SERVICE.HAS_3_DIFFERENT_FORMAT'
            )
          )
          return
        }
        if (!isValidFormat) {
          setFileError(t('SERVICE_MANAGEMENT_PAGE.SERVICE.ONLY_PNG_ALLOWED'))
        } else if (!isValidSize) {
          setFileError(t('SERVICE_MANAGEMENT_PAGE.SERVICE.FILE_SIZE_EXCEED'))
        } else {
          setFileError(t('SERVICE_MANAGEMENT_PAGE.SERVICE.INVALID_ASPECT_RATIO'))
        }
      }
      image.src = dataUrl
    }
    reader.readAsDataURL(file)
  }

  const markNameTouched = (key: 'nameEn' | 'nameAr') =>
    setTouchedNames((current) => ({...current, [key]: true}))

  const nameEnError =
    touchedNames.nameEn && values.nameEn.trim().length === 0
      ? t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NAME_EN_REQUIRED')
      : ''
  const nameArError =
    touchedNames.nameAr && values.nameAr.trim().length === 0
      ? t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NAME_AR_REQUIRED')
      : ''

  // CNT-242: the icon is optional, both when adding and when editing a category.
  const isFormComplete = values.nameEn.trim().length > 0 && values.nameAr.trim().length > 0

  const isSaveTargetKnown = mode !== 'edit' || Boolean(categoryId)
  const isReadOnly = mode === 'edit' && !isEditable
  const isFormLocked = isReadOnly || isSaving
  const canSave = isFormComplete && isSaveTargetKnown && !isSaving && !isLoadingDetail
  // The steps after Category Info only show the category's Payment data, so they stay locked until it exists
  const hasPaymentConfig = paymentConfig != null

  const buildFormData = (): FormData => {
    const formData = new FormData()
    formData.append('NameAr', values.nameAr.trim())
    formData.append('NameEn', values.nameEn.trim())
    formData.append('DescriptionAr', values.descriptionAr.trim())
    formData.append('DescriptionEn', values.descriptionEn.trim())
    formData.append('Status', String(values.isActive))
    if (iconFile) {
      formData.append('Icon', iconFile)
    }
    return formData
  }

  const handleBack = () => {
    if (currentStep === STEP_CATEGORY_INFO) {
      onHide()
      return
    }
    setCurrentStep(currentStep - 1)
  }

  const handleSave = () => {
    if (!canSave || isSavingRef.current) {
      return
    }
    isSavingRef.current = true
    setFormError('')
    setIsSaving(true)

    const callback = (res: boolean | null, err?: unknown) => {
      if (!res) {
        isSavingRef.current = false
        setIsSaving(false)
        setFormError(String(err ?? '') || t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.SAVE_FAILED'))
        return
      }
      toast.success(
        t(
          mode === 'edit'
            ? 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.UPDATE_SUCCESS'
            : 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ADD_SUCCESS'
        ),
        ToastDefaultConfig()
      )
      if (onSaved) {
        onSaved()
      }
      onHide()
    }

    if (mode === 'edit' && categoryId) {
      dispatch(
        serviceManagementAction.updateCentroCategoryInfo({
          body: {id: categoryId, body: buildFormData()},
          callback,
        })
      )
      return
    }

    dispatch(serviceManagementAction.addCentroCategoryInfo({body: buildFormData(), callback}))
  }

  return (
    <Modal
      show={isShow}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
      dialogClassName='category-modal__dialog'
      contentClassName='category-modal__content'
    >
      <div
        className={`category-modal${
          currentStep === STEP_PAYMENT ? ' category-modal--payment' : ''
        }`}
      >
        <div className='category-modal__header'>
          <h2 className='category-modal__title'>
            {t(
              mode === 'edit'
                ? 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.TITLE_EDIT'
                : 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.TITLE_ADD'
            )}
          </h2>
          <button
            type='button'
            className='category-modal__close'
            aria-label={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CLOSE')}
            disabled={isSaving}
            onClick={onHide}
          >
            <KTSVG defaultColor={true} path={ICONS.close} />
          </button>
        </div>

        <div
          className={`category-modal__body${
            isLoadingDetail ? ' category-modal__body--loading' : ''
          }`}
        >
          {isLoadingDetail && (
            <div className='category-modal__loading' role='status' aria-live='polite'>
              <span className='spinner-border' aria-hidden='true' />
              <span className='category-modal__loading-text'>{t('COMMON.LOADING')}</span>
            </div>
          )}

          <ol className='category-modal__stepper'>
            {STEP_KEYS.map((key, index) => (
              <li className='category-modal__step' key={key}>
                {index > 0 && (
                  <span
                    className={`category-modal__step-connector${
                      index <= currentStep ? ' category-modal__step-connector--complete' : ''
                    }`}
                    aria-hidden='true'
                  />
                )}
                <span
                  className={`category-modal__step-circle${
                    index < currentStep
                      ? ' category-modal__step-circle--complete'
                      : index === currentStep
                      ? ' category-modal__step-circle--active'
                      : ''
                  }`}
                  aria-hidden='true'
                >
                  {index < currentStep ? '✓' : index + 1}
                </span>
                <button
                  type='button'
                  className={`category-modal__step-label category-modal__step-link${
                    index <= currentStep ? ' category-modal__step-label--active' : ''
                  }`}
                  disabled={
                    index > STEP_TRANSPORTATION ||
                    (index > STEP_CATEGORY_INFO && !hasPaymentConfig) ||
                    isSaving ||
                    isLoadingDetail
                  }
                  aria-current={index === currentStep ? 'step' : undefined}
                  onClick={() => setCurrentStep(index)}
                >
                  {t(key)}
                </button>
              </li>
            ))}
          </ol>

          {formError.length > 0 && (
            <p className='category-modal__error' role='alert'>
              {formError}
            </p>
          )}

          {currentStep === STEP_CATEGORY_INFO && (
            <>
              <div className='category-modal__banner'>
                <span className='category-modal__banner-icon'>
                  <KTSVG defaultColor={true} path={ICONS.info} />
                </span>
                <p className='category-modal__banner-text'>
                  {t(
                    isReadOnly
                      ? 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NOT_EDITABLE_NOTICE'
                      : 'SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CUSTOM_SERVICE_NOTICE'
                  )}
                </p>
              </div>

              <div className='category-modal__grid'>
                <div className='category-modal__field'>
                  <label className='category-modal__label' htmlFor='category-name-en'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CATEGORY_NAME')}
                    <span className='category-modal__label-hint'>
                      {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ENGLISH')}
                    </span>
                    <span className='category-modal__required'>*</span>
                  </label>
                  <input
                    id='category-name-en'
                    type='text'
                    className={`category-modal__input${
                      nameEnError.length > 0 ? ' category-modal__input--invalid' : ''
                    }`}
                    placeholder={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NAME_EN_PLACEHOLDER')}
                    value={values.nameEn}
                    disabled={isFormLocked}
                    aria-invalid={nameEnError.length > 0}
                    aria-describedby={nameEnError.length > 0 ? 'category-name-en-error' : undefined}
                    onChange={(event) => setField('nameEn', event.target.value)}
                    onBlur={() => markNameTouched('nameEn')}
                  />
                  {nameEnError.length > 0 && (
                    <p className='category-modal__error' id='category-name-en-error' role='alert'>
                      {nameEnError}
                    </p>
                  )}
                </div>

                <div className='category-modal__field'>
                  <label className='category-modal__label' htmlFor='category-name-ar'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CATEGORY_NAME')}
                    <span className='category-modal__label-hint'>
                      {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ARABIC')}
                    </span>
                    <span className='category-modal__required'>*</span>
                  </label>
                  <input
                    id='category-name-ar'
                    type='text'
                    className={`category-modal__input${
                      nameArError.length > 0 ? ' category-modal__input--invalid' : ''
                    }`}
                    placeholder={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NAME_AR_PLACEHOLDER')}
                    value={values.nameAr}
                    disabled={isFormLocked}
                    aria-invalid={nameArError.length > 0}
                    aria-describedby={nameArError.length > 0 ? 'category-name-ar-error' : undefined}
                    onChange={(event) => setField('nameAr', event.target.value)}
                    onBlur={() => markNameTouched('nameAr')}
                  />
                  {nameArError.length > 0 && (
                    <p className='category-modal__error' id='category-name-ar-error' role='alert'>
                      {nameArError}
                    </p>
                  )}
                </div>

                <div className='category-modal__field'>
                  <label className='category-modal__label' htmlFor='category-desc-en'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.DESCRIPTION')}
                    <span className='category-modal__label-hint'>
                      {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ENGLISH')}
                    </span>
                  </label>
                  <textarea
                    id='category-desc-en'
                    className='category-modal__textarea'
                    value={values.descriptionEn}
                    disabled={isSaving}
                    onChange={(event) => setField('descriptionEn', event.target.value)}
                  />
                </div>

                <div className='category-modal__field'>
                  <label className='category-modal__label' htmlFor='category-desc-ar'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.DESCRIPTION')}
                    <span className='category-modal__label-hint'>
                      {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ARABIC')}
                    </span>
                  </label>
                  <textarea
                    id='category-desc-ar'
                    className='category-modal__textarea'
                    value={values.descriptionAr}
                    disabled={isSaving}
                    onChange={(event) => setField('descriptionAr', event.target.value)}
                  />
                </div>

                <div className='category-modal__field'>
                  <span className='category-modal__label'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CATEGORY_ICON')}
                  </span>
                  <input
                    ref={fileInputRef}
                    id='category-icon'
                    type='file'
                    accept='image/png'
                    className='category-modal__file-input'
                    onChange={handleFileChange}
                  />
                  <button
                    type='button'
                    className='category-modal__upload'
                    aria-label={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.UPLOAD_ICON')}
                    disabled={isSaving}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {iconPreview.length > 0 ? (
                      <img
                        className='category-modal__upload-preview'
                        src={iconPreview}
                        alt={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ICON_PREVIEW')}
                      />
                    ) : (
                      <KTSVG defaultColor={true} path={ICONS.image} />
                    )}
                  </button>
                  <p className='category-modal__hint'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.CATEGORY_ICON_HINT')}
                  </p>
                  {fileError.length > 0 && (
                    <p className='category-modal__error' role='alert'>
                      {fileError}
                    </p>
                  )}
                </div>

                <div className='category-modal__field'>
                  <span className='category-modal__label'>
                    {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.STATUS')}
                  </span>
                  <div className='category-modal__status'>
                    <button
                      type='button'
                      role='switch'
                      aria-checked={values.isActive}
                      aria-label={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ACTIVE')}
                      className={`category-modal__toggle${
                        values.isActive ? ' category-modal__toggle--on' : ''
                      }`}
                      disabled={isSaving}
                      onClick={() => setField('isActive', !values.isActive)}
                    >
                      <span className='category-modal__toggle-knob' />
                    </button>
                    <span className='category-modal__status-label'>
                      {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ACTIVE')}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
          {currentStep === STEP_PAYMENT && <CategoryPaymentSettings config={paymentConfig} />}

          {currentStep === STEP_TRANSPORTATION && (
          <>
          <p className='category-modal__description'>
            {t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.DESCRIPTION')}
          </p>

          <div className='category-modal__banner category-modal__banner--warning'>
            <span className='category-modal__banner-icon'>
              <KTSVG defaultColor={true} path={ICONS.info} />
            </span>
            <p className='category-modal__banner-text'>
              {t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.READ_ONLY_NOTICE')}
            </p>
          </div>

          <TransportationConfig dispatchMode={transportDispatchMode} />
          </>
          )}
        </div>

        <div className='category-modal__footer'>
          <button
            type='button'
            className='category-modal__btn category-modal__btn--back'
            disabled={isSaving}
            onClick={handleBack}
          >
            {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.BACK')}
          </button>
          {currentStep === STEP_CATEGORY_INFO && (
            <button
              type='button'
              className='category-modal__btn category-modal__btn--advanced'
              disabled={isFormLocked}
              onClick={onOpenAdvanced}
            >
              {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.ADVANCED')}
            </button>
          )}
          {/* Moving between steps only views them, so a read-only (B8ak) category can still go next */}
          {currentStep < STEP_TRANSPORTATION && (
            <button
              type='button'
              className='category-modal__btn category-modal__btn--next'
              disabled={
                isSaving ||
                isLoadingDetail ||
                (currentStep === STEP_CATEGORY_INFO && !hasPaymentConfig)
              }
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              {t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.NEXT')}
            </button>
          )}
          <Button
            type='button'
            variant='primary'
            className='category-modal__btn category-modal__btn--save'
            disabled={!canSave}
            onClick={handleSave}
          >
            {isSaving
              ? t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.SAVING')
              : t('ADD_ZONE_MODAL.SAVE')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ServiceCategoryModal
